import React, { useRef } from "react";
import { Circle, Group, Line, Rect, Text } from "react-konva";

const placeholderLayout = (width, height) => {
  const shortSide = Math.max(1, Math.min(width, height));
  const iconSize = Math.max(12, Math.min(96, shortSide * 0.32));
  const fontSize = Math.max(10, Math.min(26, shortSide * 0.09));
  const gap = Math.max(4, fontSize * 0.45);
  const contentHeight = iconSize + gap + fontSize * 1.2;
  const iconX = (width - iconSize) / 2;
  const iconY = Math.max(4, (height - contentHeight) / 2);
  return { iconSize, fontSize, gap, iconX, iconY };
};

const PhotoFrame = ({ obj, refCallback, onSelect, onUpdate, isSnap, grid }) => {
  const groupRef = useRef(null);
  const rectRef = useRef(null);
  const w = obj.width ?? 240;
  const h = obj.height ?? 160;
  const { iconSize, fontSize, gap, iconX, iconY } = placeholderLayout(w, h);
  const iconStroke = "#667085";

  const handleClickSelect = (event) => {
    event.cancelBubble = true;
    onSelect(event, obj.id);
  };

  const handleDragStartSelect = (event) => {
    event.cancelBubble = true;
    onSelect(event, obj.id);
  };

  return (
    <Group
      id={obj.id}
      ref={groupRef}
      x={obj.x ?? 0}
      y={obj.y ?? 0}
      rotation={obj.rotation || 0}
      draggable={obj.draggable !== false}
      dragDistance={2}
      onClick={handleClickSelect}
      onTap={handleClickSelect}
      onDragStart={handleDragStartSelect}
      onDragEnd={(event) => {
        const rawX = event.target.x();
        const rawY = event.target.y();
        const snappedX = isSnap ? Math.round(rawX / grid) * grid : rawX;
        const snappedY = isSnap ? Math.round(rawY / grid) * grid : rawY;
        onUpdate(obj.id, { x: snappedX, y: snappedY });
      }}
      clip={{ x: 0, y: 0, width: w, height: h }}
    >
      <Rect x={0} y={0} width={w} height={h} fill="#f2f4f7" listening={false} />
      <Group x={iconX} y={iconY} listening={false}>
        <Rect
          width={iconSize}
          height={iconSize * 0.72}
          cornerRadius={Math.max(2, iconSize * 0.07)}
          stroke={iconStroke}
          strokeWidth={Math.max(1.5, iconSize * 0.035)}
          listening={false}
        />
        <Circle
          x={iconSize * 0.72}
          y={iconSize * 0.2}
          radius={iconSize * 0.075}
          fill={iconStroke}
          listening={false}
        />
        <Line
          points={[
            iconSize * 0.1, iconSize * 0.61,
            iconSize * 0.34, iconSize * 0.35,
            iconSize * 0.52, iconSize * 0.52,
            iconSize * 0.66, iconSize * 0.4,
            iconSize * 0.9, iconSize * 0.61
          ]}
          stroke={iconStroke}
          strokeWidth={Math.max(1.5, iconSize * 0.035)}
          lineJoin="round"
          listening={false}
        />
      </Group>
      <Text
        x={0}
        y={iconY + iconSize * 0.72 + gap}
        width={w}
        text="PHOTO HERE"
        align="center"
        fontFamily="Arial"
        fontStyle="bold"
        fontSize={fontSize}
        fill={iconStroke}
        listening={false}
      />
      <Rect
        ref={(node) => {
          rectRef.current = node;
          if (refCallback) refCallback(node);
        }}
        x={0}
        y={0}
        width={w}
        height={h}
        fill="rgba(0,0,0,0.001)"
        stroke={obj.stroke || "#000000"}
        strokeWidth={obj.strokeWidth ?? 1}
        dash={obj.dash ?? []}
        cornerRadius={obj.cornerRadius ?? 0}
        listening={true}
        onTransformEnd={(event) => {
          const rectNode = event.target;
          const sx = rectNode.scaleX();
          const sy = rectNode.scaleY();
          const driftX = rectNode.x();
          const driftY = rectNode.y();
          const deltaRotation = rectNode.rotation();

          rectNode.scaleX(1);
          rectNode.scaleY(1);
          rectNode.x(0);
          rectNode.y(0);
          rectNode.rotation(0);

          onUpdate(obj.id, {
            x: (obj.x ?? 0) + driftX,
            y: (obj.y ?? 0) + driftY,
            width: Math.max(1, w * sx),
            height: Math.max(1, h * sy),
            rotation: (obj.rotation ?? 0) + deltaRotation
          });
        }}
      />
    </Group>
  );
};

export { placeholderLayout };
export default PhotoFrame;
